import ApiConfig from '@e74/services/ApiConfig';

const DEFAULT_TIMEOUT = 30000;
const MAX_RETRIES = 2;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withTimeout = (promise, timeoutMs) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeoutMs),
    ),
  ]);
};

const syncService = {
  getStats: async (options = {}) => {
    const {timeout = DEFAULT_TIMEOUT, retries = MAX_RETRIES} = options;

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await withTimeout(ApiConfig.sync().getStats(), timeout);
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          await sleep(Math.pow(2, attempt) * 100);
        }
      }
    }
    throw lastError;
  },

  getJobs: async (filter = {}, options = {}) => {
    const {timeout = DEFAULT_TIMEOUT, retries = MAX_RETRIES} = options;

    const params = {
      status: filter?.status,
      limit: filter?.limit,
      offset: filter?.offset,
      instanceId: filter?.instanceId,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await withTimeout(ApiConfig.sync().getJobs(params), timeout);
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          await sleep(Math.pow(2, attempt) * 100);
        }
      }
    }
    throw lastError;
  },

  getJobDetails: async (jobId, options = {}) => {
    if (!jobId) {
      throw new Error('Job ID is required');
    }

    const {timeout = DEFAULT_TIMEOUT, retries = MAX_RETRIES} = options;

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await withTimeout(
          ApiConfig.sync().getJobDetails(jobId),
          timeout,
        );
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          await sleep(Math.pow(2, attempt) * 100);
        }
      }
    }
    throw lastError;
  },

  getJobSteps: async (jobId, options = {}) => {
    if (!jobId) {
      throw new Error('Job ID is required');
    }

    const response = await syncService.getJobDetails(jobId, options);
    return {
      data: response?.data?.steps || [],
    };
  },

  getInstances: async (options = {}) => {
    const {timeout = DEFAULT_TIMEOUT, retries = MAX_RETRIES} = options;

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await withTimeout(ApiConfig.sync().getInstances(), timeout);
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          await sleep(Math.pow(2, attempt) * 100);
        }
      }
    }
    throw lastError;
  },

  startSync: async (request, options = {}) => {
    if (!request || typeof request !== 'object') {
      throw new Error('Invalid request payload');
    }

    const {timeout = DEFAULT_TIMEOUT, retries = 0} = options;

    const payload = {
      emrType: request.emrType,
      syncType: request.syncType,
      startDate: request.startDate,
      endDate: request.endDate,
      providerIds: request.providerIds,
      departmentIds: request.departmentIds
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined || payload[key] === null) {
        delete payload[key];
      }
    });

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await withTimeout(ApiConfig.sync().startJob(payload), timeout);
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          await sleep(Math.pow(2, attempt) * 100);
        }
      }
    }
    throw lastError;
  },
};

export {syncService};
export default syncService;
